/**
 * Liquiditätsplanung v1 (#1315) — the farmer-facing surface. Same shell
 * as GutschriftCheckFeedPage (LensePanel sidebar + ``?ym=`` as the
 * single month source of truth + PageLoadOverlay under its own
 * telemetry descriptor), centre = verdict line + balance chart + ledger
 * feed. The month lens drives the chart highlight band and filters the
 * ledger to the selected month. All data flows through useLiquiditaetsplanung.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Plus } from 'lucide-react';
import { LensePanel, PageLoadOverlay, useMobileSidePanel } from '@hofsync/design-system';
import {
  useLiquiditaetsplanung,
  useUpsertPlannedTransaction,
} from '../../../hooks/useLiquiditaetsplanung';
import { accountColor, accountLabel, type LiquidityAccount } from './accounts';
import { unsortedShare } from './unsortedShare';
import { formatEur } from '../../../utils/formatEur';
import { usePageLoadTelemetry } from '../../../hooks/usePageLoadTelemetry';
import { monthsToWjLensItems, formatMonthName, wjLabel, wjStartYear } from './monthLensItems';
import { buildTransactionLedgerRows, rowsInMonth, rowsInWj } from './buildLedgerRows';
import type { LedgerRow } from './buildLedgerRows';
import { periodAnchorYm, type SelectedPeriod } from './selectedPeriod';
import { wjWindowOf } from './categoryRollup';
import {
  WINDOW_MONTHS,
  addDays,
  addMonths,
  addMonthsToYm,
  firstDayOfMonth,
  isIsoDate,
  lastDayOfMonth,
} from './computeBalanceSeries';
import { todayIso } from '../../../utils/today';
import { firstTxIdByDate, markerFor } from './chartSelection';
import { categorizeAction, groupRecords, planEditId, plannedToRecord } from './transactions';
import { categoriesForPicker, planSaveErrorMessage } from './transactionForm';
import { scopeRecordsToAccount } from './scopeRecords';
import {
  DEFAULT_PRESENTATION,
  resolvePresentation,
  type ChartPresentation,
} from './monthlyColumns';
import { liquiditySummary } from './summary';
import { TOTAL_LINE_BLUE } from './chartColors';
import LiquiditySummary from './LiquiditySummary';
import BalanceChart from './BalanceChart';
import LedgerFeed from './LedgerFeed';
import CategoryFeed from './CategoryFeed';
import TransactionFormModal from './TransactionFormModal';
import type { BalanceSeriesResult, PlannedTransaction, Transaction } from './types';
import './LiquiditaetsplanungPage.css';

/** ``YYYY-MM-DD`` → ``DD.MM.`` for the Ist/Prognose orientation caption. */
const shortDay = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  });

/**
 * The newest month that still carries actual (Ist) data — the ``istHorizon``
 * month, clamped into ``availableMonths``. ``availableMonths`` also spans the
 * 12-month forecast window, so a plain ``.at(-1)`` would land ~a year ahead on a
 * Prognose-only month; instead pick the latest available month at/before the
 * Ist/Prognose boundary (the same value the "Ist bis 30.06." caption reads).
 * Falls back to the oldest month when there is no boundary (empty/neutral series).
 */
function latestIstMonth(availableMonths: string[], istHorizon: string): string {
  const boundary = istHorizon.slice(0, 7); // 'YYYY-MM-DD' → 'YYYY-MM'; '' when neutral
  if (boundary) {
    // availableMonths is sorted ascending — walk back to the newest Ist month.
    for (let i = availableMonths.length - 1; i >= 0; i--) {
      if (availableMonths[i] <= boundary) return availableMonths[i];
    }
  }
  return availableMonths[0] ?? '';
}

/** Selected month: explicit ?ym= → first problem month → latest Ist (actual-data) month. */
export function resolveSelectedMonth(
  ymParam: string | null,
  firstProblemMonth: string | null,
  availableMonths: string[],
  istHorizon: string,
): string {
  return ymParam ?? firstProblemMonth ?? latestIstMonth(availableMonths, istHorizon);
}

/**
 * #2254 — the selected period: a month (``?ym=``) or a whole Wirtschaftsjahr
 * (``?wj=YYYY``, start-year form matching the ``wj-<startYear>`` lens ids).
 * ``?ym=`` wins when both are present (the more specific claim); a garbage or
 * unknown ``?wj=`` falls through to the month chain — so the #2199 default
 * resolve stays byte-identical whenever no valid year is selected.
 */
export function resolveSelectedPeriod(
  ymParam: string | null,
  wjParam: string | null,
  firstProblemMonth: string | null,
  availableMonths: string[],
  istHorizon: string,
  wjStartMonth: number,
): SelectedPeriod {
  // ``?ym=`` wins on PRESENCE (``== null``), not truthiness: a degenerate
  // ``?ym=&wj=2026`` keeps the documented month precedence (review r1).
  if (ymParam == null && wjParam && /^\d{4}$/.test(wjParam)) {
    const startYear = Number(wjParam);
    // Valid only when that WJ actually has lens months (mirrors resolveKonto's
    // known-id check); an unknown year is tolerated → month chain.
    if (availableMonths.some((ym) => wjStartYear(ym, wjStartMonth) === startYear)) {
      return { kind: 'wj', startYear };
    }
  }
  return {
    kind: 'month',
    ym: resolveSelectedMonth(ymParam, firstProblemMonth, availableMonths, istHorizon),
  };
}

/** The feed view: the chronological ledger (default) or the category budget lens. */
export type LedgerView = 'buchungen' | 'kategorien';

/** ``?view=`` → a valid view; default + unknown ⇒ ``'buchungen'`` (nothing changes
 *  until a farmer opts in). Mirrors ``resolveSelectedMonth``'s tolerant resolve. */
export function resolveView(viewParam: string | null): LedgerView {
  return viewParam === 'kategorien' ? 'kategorien' : 'buchungen';
}

/** The "Alle Konten" aggregate scope id (no specific account selected). */
export const ALL_ACCOUNTS = 'alle';

/** ``?konto=`` → a known account id, else the ``'alle'`` aggregate (#2005). Unknown
 *  or absent ⇒ Alle Konten, mirroring ``resolveView``'s tolerant resolve. */
export function resolveKonto(kontoParam: string | null, accounts: LiquidityAccount[]): string {
  return kontoParam && accounts.some((a) => a.id === kontoParam) ? kontoParam : ALL_ACCOUNTS;
}

/** The series value on ``asOf`` — the last point at/before it, so a date with no
 *  own point (a weekend, a gap) reads the balance still standing on it. Null for
 *  an empty series, or for an ``asOf`` before the series starts. */
function balanceAsOf(series: BalanceSeriesResult, asOf: string): number | null {
  let bal: number | null = null;
  for (const p of series.dailySeries) {
    if (p.date > asOf) break;
    bal = p.balanceEur;
  }
  return bal;
}

/**
 * HOF-5 — the date the Kontostand figures are read at: the END of the selected
 * period, so picking June shows what the accounts held on 30.06. rather than
 * today's balance under a June heading (which is what every account row used to
 * show, at every selection).
 *
 * Clamped to the last day the curve covers, so a selection past the projection
 * horizon reads the farthest point that exists instead of silently falling off
 * the end and rendering an em dash. Falls back to ``today`` when nothing is
 * selected (the empty-``ym`` state) — the pre-HOF-5 behaviour.
 */
export function breakdownAsOfDate(
  period: SelectedPeriod,
  wjStartMonth: number,
  seriesEnd: string,
  today: string,
): string {
  const window =
    period.kind === 'wj'
      ? wjWindowOf(periodAnchorYm(period, wjStartMonth), wjStartMonth)
      : { startYm: period.ym, endYm: period.ym };
  if (!window.endYm) return today;
  const periodEnd = lastDayOfMonth(window.endYm);
  // The period the farmer is standing IN reads at TODAY, not at its own end.
  // Otherwise simply opening the page — which lands on the current month —
  // would replace the farm's real balance with a projection of where it might
  // be in three weeks, which is not what a headline „Kontostand" should be.
  // Past periods read at their end (a settled figure); future ones at their end
  // too (a projection, and labelled as one).
  const inPeriod = firstDayOfMonth(window.startYm) <= today && periodEnd >= today;
  const asOf = inPeriod ? today : periodEnd;
  return seriesEnd && asOf > seriesEnd ? seriesEnd : asOf;
}

/** Earliest/latest ISO ``value_date`` of the OTHER plan rows (all rows except the
 *  one being edited), or ``undefined`` when there are none. Drives the fallback
 *  date bounds. Malformed dates are skipped — they never anchor the projection
 *  (see ``defaultConfig``), so they must not constrain the window either. */
export type FallbackOtherSpan = { earliest: string; latest: string } | undefined;

/** The last ``isActual`` point's date = the projection anchor (history is walked
 *  backward FROM it, the projection forward AFTER it). Falls back to ds[0] only
 *  if nothing is flagged actual. */
function anchorDateOf(series: BalanceSeriesResult): string | undefined {
  const ds = series.dailySeries;
  if (ds.length === 0) return undefined;
  for (let i = ds.length - 1; i >= 0; i--) {
    if (ds[i].isActual) return ds[i].date;
  }
  return ds[0].date;
}

/**
 * Valid plan-date window for the create/edit form, derived so the phantom-row
 * guard works in BOTH the stored-config and the synthesized fallback (no-config)
 * modes. A plan row outside the projected window is dropped from the curve but
 * ``buildLedgerRows`` still emits a phantom 0-balance ledger row, so the form
 * must reject it (codex #1364 P2a).
 *
 * **Stored config** (``series`` has a fixed, persisted anchor): the window is the
 * projected range ``(anchor, horizon end]``. Lower bound = the anchor (matching
 * the curve's strict ``value_date > anchorIso`` filter — a row on the anchor day
 * is not projected). Upper bound = the last series day. The anchor is read off
 * the series (last ``isActual`` point), NOT ``dailySeries[0]`` — with pre-anchor
 * bank/statement history the series starts at the history start, before the
 * anchor.
 *
 * **Fallback** (no config): the synthetic anchor and horizon FLOAT — re-derived
 * as ``earliest(planned) − 1`` and ``+ WINDOW_MONTHS`` on every recompute. So an
 * edit is a phantom iff the RESULTING full plan set no longer fits one
 * ``WINDOW_MONTHS`` window: the new earliest re-anchors and any row past
 * ``earliest − 1 + WINDOW_MONTHS`` drops off the curve. Given the OTHER rows'
 * span ``[oEarliest, oLatest]`` (``other``), the edited row's value ``D`` keeps
 * the whole set in one window iff
 *   ``D ∈ ( addMonths(oLatest, −WINDOW_MONTHS), addDays(addMonths(oEarliest, WINDOW_MONTHS), −1) ]``
 * — the lower bound stops an early re-anchor from dropping ``oLatest``; the upper
 * bound stops a late row from falling outside the anchor pinned by ``oEarliest``.
 * With no other rows (create-first / edit-sole) the edited row defines its own
 * window → no bounds.
 *
 * Returns ``undefined`` when there is nothing to bound against (empty series in
 * config mode, or no other rows in fallback mode → the form imposes no bounds).
 */
export function deriveDateBounds(
  series: BalanceSeriesResult,
  anchored: boolean,
  other?: FallbackOtherSpan,
  today?: string,
): { minExclusive: string; max: string } | undefined {
  if (anchored) {
    const ds = series.dailySeries;
    if (ds.length === 0) return undefined;
    // #3241: prefer the CONSERVATIVE boundary. This guard asks "would the curve
    // project this row?", which is a correctness question, and under „Alle Konten"
    // the last ``isActual`` point follows the WIDENED label. Bounding on that
    // rejects a plan the curve does project (one past a dormant account's own
    // horizon) with a hard save-time error. ``verdictHorizon`` is the strict MIN,
    // below which no account projects — byte-identical to the pre-#3241 bound, and
    // equal to ``anchorDateOf`` for every per-account series.
    const anchor = series.verdictHorizon !== '' ? series.verdictHorizon : anchorDateOf(series)!;
    return { minExclusive: anchor, max: ds[ds.length - 1].date };
  }
  if (!other) return undefined; // no other rows → the edited row defines its own window
  // Mirror the projection's exact horizon arithmetic — anchor = earliest − 1 day,
  // series end = addMonths(anchor, WINDOW_MONTHS) — so the bound never diverges
  // from what the curve actually covers (month-add clamps day-of-month, so adding
  // months on the raw date vs. on the anchor differ around leap/short months;
  // codex P2). max keeps a later edit inside the anchor pinned by ``earliest``;
  // minExclusive keeps an earlier re-anchor from dropping ``latest`` off the end.
  const anchorMax = addMonths(addDays(other.earliest, -1), WINDOW_MONTHS);
  // The projection horizon now also extends to the END of the (today+12mo) month
  // (see computeBalanceSeries), so the form must accept rows up to there too.
  const todayMax =
    today && today > addDays(other.earliest, -1)
      ? lastDayOfMonth(addMonthsToYm(today.slice(0, 7), WINDOW_MONTHS))
      : anchorMax;
  return {
    minExclusive: addDays(addMonths(other.latest, -WINDOW_MONTHS), -1),
    max: todayMax > anchorMax ? todayMax : anchorMax,
  };
}

/** Calendar ym → its Wirtschaftsjahr lens id (matches monthLensItems). */
function wjIdOf(ym: string, wjStartMonth: number): string {
  return `wj-${wjStartYear(ym, wjStartMonth)}`;
}

export default function LiquiditaetsplanungPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const ymParam = searchParams.get('ym');
  const wjParam = searchParams.get('wj');

  const {
    config,
    planned,
    records,
    transactions,
    series,
    availableMonths,
    accounts,
    accountSeries,
    noBankAccountConnected,
    dataQuality,
    isLoading,
    error,
  } = useLiquiditaetsplanung();

  const wjStartMonth = config?.wj_start_month ?? 7;
  // #2254 — the selected period (month OR whole Wirtschaftsjahr). The month
  // branch delegates to resolveSelectedMonth, so the #2199 default chain is
  // untouched whenever no valid ?wj= is present.
  const period = useMemo(
    () =>
      resolveSelectedPeriod(
        ymParam,
        wjParam,
        series.firstProblemMonth,
        availableMonths,
        series.istHorizon,
        wjStartMonth,
      ),
    [ymParam, wjParam, series.firstProblemMonth, availableMonths, series.istHorizon, wjStartMonth],
  );
  // The selected month — empty under a Wirtschaftsjahr selection. Consumers
  // that are month-only (chart click target, month band) read this; period-
  // aware consumers read ``period`` instead.
  const selectedYm = period.kind === 'month' ? period.ym : '';

  // The feed view: chronological ledger (default) or the category-grouped budget
  // lens (#1617). Pure view state, persisted in ?view= so it deep-links; absent /
  // unknown ⇒ 'buchungen' so nothing changes until a farmer opts in.
  const view = resolveView(searchParams.get('view'));
  const handleViewChange = useCallback(
    (next: LedgerView) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        // Keep the default implicit (no ?view=) so the URL stays clean.
        if (next === 'buchungen') params.delete('view');
        else params.set('view', next);
        return params;
      });
    },
    [setSearchParams],
  );

  // #2684 Part B — the chart's presentation (Kurve | Säulen). Orthogonal to
  // every other param: ?ym=/?wj= say WHICH period is selected, ?pm= only how the
  // chart draws it. Binary with a stable default, so it mirrors ?view=/?konto=
  // (and Preisspiegel's own ?pm=): the default stays implicit in the URL.
  const presentation = resolvePresentation(searchParams.get('pm'));
  const handlePresentationChange = useCallback(
    (next: ChartPresentation) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next === DEFAULT_PRESENTATION) params.delete('pm');
        else params.set('pm', next);
        return params;
      });
    },
    [setSearchParams],
  );

  // #2005 account scope — the selected bank account, mirroring ?view= as ?konto=.
  // Only meaningful with more than one account; single-account tenants stay on the
  // Alle Konten aggregate (which then IS the one account) with no selector (D4).
  const isMultiAccount = accounts.length > 1;
  const selectedKonto = isMultiAccount
    ? resolveKonto(searchParams.get('konto'), accounts)
    : ALL_ACCOUNTS;
  const handleKontoChange = useCallback(
    (next: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next === ALL_ACCOUNTS) params.delete('konto');
        else params.set('konto', next);
        return params;
      });
    },
    [setSearchParams],
  );

  // Category-view fold state, lifted here so it survives the Buchungen⇄Kategorien
  // toggle (CategoryFeed unmounts on switch). Groups start collapsed (a group not in
  // the set is folded); the open L2 category resets on a month switch (mirrors the
  // ledger), while group folds persist across both view and month switches.
  const [catExpandedGroups, setCatExpandedGroups] = useState<Set<string>>(new Set());
  const [catOpenId, setCatOpenId] = useState<string | null>(null);
  // L1 is a single-open accordion: opening a group folds whatever else was open, so
  // at most one group is unfolded at a time. Switching to a different group also
  // resets the open L2 (its previously-open category lives in a now-hidden group).
  const toggleCatGroup = useCallback((id: string) => {
    setCatExpandedGroups((cur) => (cur.has(id) ? new Set() : new Set([id])));
    setCatOpenId((cur) => (catExpandedGroups.has(id) ? cur : null));
  }, [catExpandedGroups]);
  const toggleCatOpen = useCallback((id: string) => {
    setCatOpenId((cur) => (cur === id ? null : id));
  }, []);
  // The open L2 category persists across month switches (it only resets when its group
  // closes or a different group opens — see toggleCatGroup) so you can step through the
  // months watching the same category's figures change.

  const pageLoadOverlay = usePageLoadTelemetry({
    page: 'liquiditaetsplanung',
    view:
      period.kind === 'wj'
        ? `liquiditaetsplanung:wj=${period.startYear}`
        : `liquiditaetsplanung:ym=${selectedYm}`,
    isLoading,
  });

  // Persist the resolved default into ?ym= once (deep-linkable), mirroring
  // the gutschrift-check auto-advance — strictly on the initial load. A VALID
  // year selection skips the persist entirely (#2254): stamping a month here
  // would win the ym-over-wj precedence and silently swallow a deep-linked
  // year selection. An INVALID ?wj= (unknown year / garbage) resolved to the
  // month chain still persists, so the resolved default stays pinned in the
  // URL instead of drifting with later refetches (review r1).
  const isWjSelected = period.kind === 'wj';
  useEffect(() => {
    if (ymParam || isWjSelected || isLoading || !selectedYm) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('ym', selectedYm);
      return next;
    });
  }, [ymParam, isWjSelected, isLoading, selectedYm, setSearchParams]);

  // Month and Wirtschaftsjahr selections are mutually exclusive in the URL:
  // picking one deletes the other (#2254). Both push (no {replace}) so
  // back/forward steps through the selection history.
  const handleMonthChange = useCallback(
    (ym: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('ym', ym);
        next.delete('wj');
        return next;
      });
    },
    [setSearchParams],
  );
  const handleWjChange = useCallback(
    (wjId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('wj', wjId.replace(/^wj-/, ''));
        next.delete('ym');
        return next;
      });
    },
    [setSearchParams],
  );

  // The expanded WJ in the lens follows the selection (LensePanel auto-expands
  // ``selectedItem``): the selected year itself, or the selected month's year.
  const expandedWj =
    period.kind === 'wj'
      ? `wj-${period.startYear}`
      : selectedYm
        ? wjIdOf(selectedYm, wjStartMonth)
        : '';

  const mobilePanel = useMobileSidePanel();

  // #2254 — a bare WJ-header tap now SELECTS the year (it used to only
  // expand/collapse). The #1326 mobile-close suppression is gone with it: a
  // WJ tap is a real selection, so closing the drawer on it is correct —
  // exactly like a month tap. Re-tapping the already-selected WJ (the old
  // collapse gesture) is a no-op, so it can't stack duplicate history
  // entries that make Back appear dead (review r1).
  const handleLensSelect = useCallback(
    (itemId: string, subItemId: string | null) => {
      if (subItemId) {
        handleMonthChange(subItemId);
        return;
      }
      if (period.kind === 'wj' && itemId === `wj-${period.startYear}`) return;
      handleWjChange(itemId);
    },
    [handleMonthChange, handleWjChange, period],
  );

  const handleMobileClose = useCallback(() => {
    mobilePanel?.close();
  }, [mobilePanel]);

  const lensItems = useMemo(
    () => monthsToWjLensItems(availableMonths, wjStartMonth),
    [availableMonths, wjStartMonth],
  );

  const today = todayIso();
  const todayYm = today.slice(0, 7);

  // The chart's WJ highlight band (#2254) — memoized so the object identity is
  // stable across unrelated page re-renders (review r1).
  const wjBandRange = useMemo(
    () =>
      period.kind === 'wj'
        ? wjWindowOf(periodAnchorYm(period, wjStartMonth), wjStartMonth)
        : undefined,
    [period, wjStartMonth],
  );

  // The active account scope: a specific account's per-account view (own series +
  // limit + istHorizon), or ``null`` for the Alle Konten aggregate (the hook's
  // top-level series already IS the N>1 total). Defined here — above the ledger —
  // because the running-balance column reads THIS account's series (#2211).
  const activeAccountView = useMemo(
    () =>
      selectedKonto === ALL_ACCOUNTS
        ? null
        : accountSeries?.find((v) => v.account.id === selectedKonto) ?? null,
    [selectedKonto, accountSeries],
  );

  // #2211 feed scoping — selecting a Konto re-scopes EVERYTHING below the chart
  // (Buchungen, Kategorien, the unsorted chip, SALDO, the running KONTOSTAND, the
  // KK-limit flags), not just the headline + curve. Scope the fact-sourced RECORDS
  // (pre-grouping) so ``unsortedShare`` — which groups internally — stays consistent
  // with the ledger. Under Alle Konten ``scopeRecordsToAccount`` returns the INPUT
  // reference, so ``scopedRecords === records`` and ``scopedTransactions`` reuses the
  // hook's ``transactions`` reference — byte-identical to today (no ``.filter`` clone
  // → LedgerFeed's ``[rows]``-keyed effects don't spuriously reset).
  const scopedRecords = useMemo(
    () => scopeRecordsToAccount(records, selectedKonto, accounts),
    [records, selectedKonto, accounts],
  );
  const scopedTransactions = useMemo(
    () => (scopedRecords === records ? transactions : groupRecords(scopedRecords)),
    [scopedRecords, records, transactions],
  );
  // The account's own balance series (with its own istHorizon + kkLimitEur) drives
  // the KONTOSTAND column, the Ist/Prognose divider and the below-limit flags; the
  // tenant total (``series``) does so under Alle Konten. This is THE series for
  // the selected scope — the ledger, the summary, the verdict and the chart all
  // read it, so their KK-limit and Ist horizon cannot drift apart.
  const scopedSeries = activeAccountView?.series ?? series;

  // One ledger row per lifecycle transaction (transaction-records): the
  // ✓⏳⚠＋ state is derived from which records it carries + planned↔payed
  // agreement, and the match is the authored transaction_id (no runtime matcher).
  // A planned-only row inside an already-received statement period (≤ anchor)
  // with no bank confirmation is flagged "did not happen" (greyed, zero balance
  // effect); the current, unreconciled month (after the anchor) is plan-driven.
  //
  // #2211 balance-column caveat (NOT "fixed" — accepted): the KONTOSTAND is looked
  // up per date from ``scopedSeries.dailySeries`` (cash_flow-sourced engine records)
  // while the ledger ROWS are fact-sourced. On WF they are 1:1 (each bank fact IS a
  // cash_flow's projection), so figures line up; a date absent from the account's
  // series falls back to ``?? 0`` (``buildLedgerRows.ts:132``) exactly as an
  // out-of-window row does today — no attempt to reconcile the two sources here.
  const ledgerRows = useMemo(
    () => buildTransactionLedgerRows(scopedTransactions, scopedSeries, scopedSeries.istHorizon),
    [scopedTransactions, scopedSeries],
  );
  // The ledger lists only the period selected in the lens panel — a month, or
  // a whole Wirtschaftsjahr (#2254). Clicking the chart selects a month → the
  // list follows. Month mode with an empty ym → full record (unchanged).
  const periodRows = useMemo(
    () =>
      period.kind === 'wj'
        ? rowsInWj(ledgerRows, period.startYear, wjStartMonth)
        : rowsInMonth(ledgerRows, selectedYm),
    [ledgerRows, period, selectedYm, wjStartMonth],
  );

  // #2005 unsorted-flows affordance: the still-uncategorised share of the selected
  // Wirtschaftsjahr (all Kontoauszug-derived flows land as ``unsorted``). Surfaces a
  // header chip that deep-links into the Buchungen ledger's kategorisieren flow.
  // Under a year selection (#2254) the WJ is the selection itself; both branches
  // share ONE membership encoding (wjStartYear) so they can never drift (review r1).
  const wjMonths = useMemo(() => {
    if (period.kind === 'month' && !selectedYm) return availableMonths;
    const targetStartYear =
      period.kind === 'wj' ? period.startYear : wjStartYear(selectedYm, wjStartMonth);
    return availableMonths.filter((ym) => wjStartYear(ym, wjStartMonth) === targetStartYear);
  }, [availableMonths, period, selectedYm, wjStartMonth]);
  const unsorted = useMemo(() => unsortedShare(scopedRecords, wjMonths), [scopedRecords, wjMonths]);

  // The per-account balance-at-today breakdown in the summary. It doubles as the
  // account switcher (#2160), so it must render in EVERY multi-account scope, not
  // just Alle Konten: the ``activeAccountView`` guard is dropped (keeping it blanked
  // the switcher the instant a farmer picked an account, stranding them). Prepend an
  // "Alle Konten — Summe" row keyed to the top-level ``series`` (the N>1 total) with
  // the bold-line colour, so the blue chart line is explained by a row the farmer
  // actually reads.
  // HOF-5 — read at the END of the SELECTED period, not unconditionally at today.
  const breakdownAsOf = useMemo(
    () =>
      breakdownAsOfDate(
        period,
        wjStartMonth,
        series.dailySeries[series.dailySeries.length - 1]?.date ?? '',
        today,
      ),
    [period, wjStartMonth, series.dailySeries, today],
  );
  const accountBreakdown = useMemo(() => {
    if (!accountSeries) return undefined;
    return [
      {
        id: ALL_ACCOUNTS,
        label: 'Alle Konten — Summe',
        color: TOTAL_LINE_BLUE,
        // ``balanceAsOf(series, …)`` keeps the Summe figure derived like the
        // per-account rows; it can differ from the hero's ``currentBalanceEur`` in
        // rare data states (different derivation) — a visible mismatch is expected.
        balanceEur: balanceAsOf(series, breakdownAsOf),
      },
      ...accountSeries.map((v) => ({
        id: v.account.id,
        label: accountLabel(v.account),
        color: accountColor(v.account.colorIndex),
        balanceEur: balanceAsOf(v.series, breakdownAsOf),
      })),
    ];
  }, [accountSeries, series, breakdownAsOf]);

  // #2211 modal + row-detail wiring — multi-account tenants ONLY. The Konto a new
  // plan preselects: the currently-selected Konto, else the overlay-primary (so
  // "Alle Konten" turns D2's silent routing into a VISIBLE default without forcing
  // a choice). ``undefined`` for single-account tenants → the modal renders no Konto
  // select and no account_id is persisted (single-account byte-identity).
  const overlayPrimaryId = accounts.find((a) => a.isOverlayPrimary)?.id;
  const defaultAccountId = isMultiAccount
    ? selectedKonto === ALL_ACCOUNTS
      ? overlayPrimaryId
      : selectedKonto
    : undefined;
  // account_id → label lookup for the LedgerFeed Konto row. Built only for
  // multi-account tenants; single-account passes ``undefined`` so no Konto row shows.
  const accountLabelById = useMemo(
    () => (isMultiAccount ? new Map(accounts.map((a) => [a.id, accountLabel(a)])) : undefined),
    [isMultiAccount, accounts],
  );

  // Fixed-window chart + chart↔ledger selection. ``selectedTxId`` is the single
  // source of truth shared by the chart marker and the highlighted ledger row.
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);
  const txByDate = useMemo(() => firstTxIdByDate(ledgerRows), [ledgerRows]);
  // The dot sits on the scope's own curve, so it never floats above/below the line.
  const selectedMarker = useMemo(
    () => markerFor(selectedTxId, ledgerRows, scopedSeries.dailySeries),
    [selectedTxId, ledgerRows, scopedSeries.dailySeries],
  );

  // Plain-language header figures (current balance, breach, Ist→Prognose dates)
  // for the selected scope.
  // HOF-5 — the balance is read at ``breakdownAsOf`` (the selected period's
  // reference date, = today whenever that period contains today), so the hero
  // figure and the per-account rows answer the same question and the header's
  // date qualifier is true of BOTH. ``liquiditySummary``'s third argument is
  // used for exactly that lookup; the breach/overrun scan and the Ist/Prognose
  // caption dates are driven by the horizons and are unaffected.
  const summary = useMemo(
    () => liquiditySummary(scopedSeries, scopedSeries.istHorizon, breakdownAsOf),
    [scopedSeries, breakdownAsOf],
  );

  // HOF-5 — the Ist-horizon LAG, when the farm has one: the rendered boundary is
  // the latest Konto's, ``verdictHorizon`` the earliest, so a gap between them
  // means at least one Konto's bank data stops sooner than the caption's date.
  // Empty for every single-account scope (the two are equal by construction) and
  // for a farm whose Konten are all equally current — no clause, no noise.
  const lagUntilDate =
    scopedSeries.verdictHorizon !== '' &&
    scopedSeries.istHorizon !== '' &&
    scopedSeries.verdictHorizon < scopedSeries.istHorizon
      ? scopedSeries.verdictHorizon
      : '';

  // +Neu / edit / categorize form. ``form`` carries the mode + the row being edited;
  // the mutation updates the cache optimistically so the curve + verdict recompute
  // the instant the form is saved (demo beat 3). The mode is set at the ONE place
  // each act starts — ``+Neu`` (create), the row pencil (edit), ``handleCategorize``
  // (categorize) — never re-derived downstream (#2678).
  const [form, setForm] = useState<{
    mode: 'create' | 'edit' | 'categorize';
    initial?: PlannedTransaction;
    /** #3152 — this act's record GROUPS onto an existing transaction: the
     *  grouping create (deterministic ``liq-plan-<txid>`` id from
     *  ``planEditId``) or an edit of a plan whose transaction also holds a
     *  contracted/payed sibling. The phantom-row guard's future-only bound may
     *  relax for it exactly as for ``categorize`` (see ``effectiveBounds``) —
     *  the sibling record anchors the row, so it can never phantom. An explicit
     *  flag with exactly two writers — ``handleCategorize``'s payed-less branch
     *  and ``editPlanned`` — each setting it at the one place its act starts,
     *  from the ROW's actual record composition; never re-derived downstream
     *  from ``initial.derived_fact_id`` (#2678: mode/state is set at the one
     *  place the act starts). */
    grouping?: boolean;
  } | null>(null);
  const upsert = useUpsertPlannedTransaction();
  const handleFormSubmit = useCallback(
    (input: Parameters<typeof upsert.mutate>[0]) => {
      upsert.mutate(input, { onSuccess: () => setForm(null) });
    },
    [upsert],
  );
  // #3152 — a FAILED save's user-facing message. Before this the 422/502 path
  // was SILENT (the modal only closes on success; ``onError`` only rolls the
  // optimistic cache back), which is how the pre-widening colon-id 422 shipped
  // unnoticed. The modal renders it dismissibly and clears it on the next
  // submit or any field change; the cache rollback stays as-is.
  const serverError = upsert.isError ? planSaveErrorMessage(upsert.error) : undefined;
  // Close = end of the modal SESSION, so the mutation's error state goes with it
  // (#3152 review): ``upsert.isError`` survives a bare ``setForm(null)``, and the
  // next modal would open with a stale "Speichern fehlgeschlagen" alert for an
  // attempt that never happened in that session.
  const closeForm = useCallback(() => {
    setForm(null);
    upsert.reset();
  }, [upsert]);

  // "kategorisieren" on an ＋ ungeplant bank line → open the create form
  // prefilled with its amount/direction/label AND the booked line's own date +
  // note (#1612 sub-04): the transaction already happened, so the farmer must not
  // hand-pick a date or lose an existing note. The payed row's transaction_id
  // rides along as a deterministic ``liq-plan-<txid>`` edit id so the saved plan
  // GROUPS with the existing payed record (flips the row out of ungeplant)
  // instead of minting a fresh, un-grouped transaction (#1494). Because the saved
  // record is absorbed into the payed transaction's ledger row, its (past) date can
  // never become a phantom — so the future-only window is relaxed for this flow
  // (see ``effectiveBounds`` below), unlike +Neu / edit.
  const handleCategorize = useCallback((row: LedgerRow) => {
    // Prefill the volume × price from the booked (payed) record so a harvest
    // line keeps its t × €/t breakdown when the farmer categorizes it.
    const payed = row.transaction?.payed;
    // #3140 — the payed-less fallback. Until #3140 this branch was correct and
    // UNREACHABLE (LedgerFeed gated on `ungeplant`, and categoryRollup's
    // phantom-booking guard never lists a contracted-only transaction), so every
    // `payed?.…` below silently yielded undefined for the one shape that now
    // reaches it. The contracted record carries all three, so read them from it
    // rather than shipping a form with no date, no period and no account.
    const commitment = payed ?? row.transaction?.contracted;
    setForm({
      // #2678: a booked bank line gets the first-class ``categorize`` act — its facts
      // render read-only and its Datum is pinned to the booking. #3065 UPDATE: this
      // is genuinely a live branch, not a total-function guard — ``categorizeAction``
      // routes BOTH an ``ungeplant`` payed-only row AND a ``geplant`` contracted-only
      // UNPAID row here (the latter deliberately kept as the real, currently-working
      // "plan around a still-open Rechnung" feature), and only the first of those has
      // a ``payed`` record. A payed-less row genuinely is not the ``categorize`` act:
      // it has no booked facts to show and no booked date to pin, and ``categorize``
      // renders no Datum control, so mislabelling one would leave the farmer unable
      // to date it — hence the ``payed ? 'categorize' : 'create'`` branch below. It
      // also keeps the selected set EXACTLY the one the deleted ``isCategorizeFlow``
      // inference picked — ``payed`` is what mints the grouping ``derived_fact_id``
      // that inference keyed on.
      mode: payed ? 'categorize' : 'create',
      // #3152 — the payed-less commitment shape is a GROUPING create: it carries
      // the deterministic grouping id (below) and may therefore relax the
      // future-only date bound (see ``effectiveBounds``), while KEEPING its Datum
      // control — a still-open expectation may legitimately be re-dated, which is
      // why it cannot simply become ``mode: 'categorize'`` (no Datum control).
      ...(!payed && commitment ? { grouping: true } : {}),
      initial: {
        kind: 'planned',
        // Seed with the line's DERIVED category (from the payed/canonical record),
        // not a hardcoded 'unsorted' — saving without re-picking must PRESERVE the
        // displayed category, not silently downgrade the grouped record (#1612).
        ledger_category: row.transaction?.ledger_category ?? 'unsorted',
        label: row.label,
        direction: row.amountEur >= 0 ? 'income' : 'expense',
        amount_eur: Math.abs(row.amountEur),
        qty: payed?.qty,
        unit_price_eur: payed?.unit_price_eur,
        // Carry the bank line's own date + period (no re-pick) and its note (if any).
        // A truly-no-payed line yields an empty date (no crash); a no-note line yields
        // an empty Notiz (nothing to carry).
        value_date: commitment?.value_date ?? '',
        period_ym: commitment?.value_date ? commitment.value_date.slice(0, 7) : '',
        notes: commitment?.notes,
        // Carry the booked line's OWN account so a bank row on a non-primary Konto
        // preselects that Konto — not the »Alle Konten« overlay-primary default —
        // and saving without re-picking never silently re-homes the plan onto the
        // wrong account partition (#2256). The modal's ``?? defaultAccountId`` still
        // covers a defensive account-less fact.
        account_id: commitment?.account_id,
        // No ``counterparty_id`` here (#2678 c) — and deliberately so: this synthetic
        // ``initial`` describes a booking that has NO plan yet, so there is no farmer
        // link to carry. The already-linked case reaches the modal through ``edit``
        // (``row.planned``, which does carry it). The exposure is latent, not live:
        // ``planEditId(payed)`` is deterministic, so a row that DID hold a link and
        // re-entered through this path would be overwritten unlinked — but the ledger
        // offers "kategorisieren" on ``ungeplant`` rows only, and
        // ``handleCategoryTxClick`` routes planned rows to ``edit``. ``qty_meta`` has
        // carried the identical pre-existing exposure since #1612.
        source: 'user',
        // Group the new plan with the existing transaction (no duplicate). For a
        // payed row this is the #1494 flow unchanged; for the payed-less #3140
        // shape, planEditId(contracted) mints the entity-scheme grouping id
        // (``liq-plan-wibill-inv:<n>``) the #3152-widened backend now accepts —
        // plannedToRecord's strip folds the saved plan onto the expectation's row.
        ...(commitment ? { derived_fact_id: planEditId(commitment) } : {}),
      },
    });
  }, []);

  // Click a transaction in the Kategorien view → open the SAME modal as Buchungen.
  // Map the transaction to its ledger row (1:1 by transaction id — both the category
  // view and the ledger derive from the same records) and reuse the existing paths,
  // gated by the SAME shared predicate ``LedgerFeed``'s own gate is built on
  // (``categorizeAction``, #3065) — so the two entry points can no longer drift
  // apart the way they did after #3026 widened which rows read bestaetigt/
  // abweichend: a row with a plan opens the edit form; an ungeplant (actual-only)
  // or a contracted-only UNPAID row opens the categorize/create form; a settled
  // contracted+payed row (no plan — its own document already categorised it) is a
  // no-op.
  const ledgerRowByTxId = useMemo(() => {
    const m = new Map<string, LedgerRow>();
    for (const r of ledgerRows) m.set(r.id, r);
    return m;
  }, [ledgerRows]);
  // #3172 (review r2) — the grouping lookup reads the UNSCOPED transactions, not
  // ``ledgerRows``. ``scopeRecordsToAccount`` routes each record by its OWN
  // account_id, so a plan that drifted onto Konto B while its sibling sits on
  // Konto A has NO sibling inside scope B's grouping: keyed off the scoped rows,
  // the flag would vanish there and hand back the very Konto select and
  // Einnahme/Ausgabe toggle this issue withdraws — on the shared ``liq-plan-…``
  // id, i.e. on exactly the corrupted population #3172 exists to repair. Whether
  // a record GROUPS is a property of the record, not of the Konto currently on
  // screen, so it is resolved from the full set.
  const transactionById = useMemo(() => {
    const m = new Map<string, Transaction>();
    for (const t of transactions) m.set(t.id, t);
    return m;
  }, [transactions]);
  // #3152 review — the ONE edit entry point (both the ledger pencil and the
  // Kategorien click route here). The bounds relaxation follows the RECORD, not
  // the act: an edit of a plan that GROUPS (its transaction also holds a
  // contracted or payed record) can never re-create a phantom — the sibling
  // anchors the row — so it earns the same relaxation as the grouping create.
  // Without this, every grouped past-dated plan (all 4.769 WF expectations are
  // past-dated) would be un-editable in place the moment it was saved. A
  // planned-ONLY row (the "did-not-happen" shape) stays un-relaxed: dropping
  // its bound would let Save re-persist the date ``computeBalanceSeries`` drops
  // (#1612 gate 1). ``grouping`` is set HERE, where the act starts (#2678).
  const editPlanned = useCallback(
    (p: PlannedTransaction) => {
      const tx = transactionById.get(plannedToRecord(p).transaction_id);
      // #3172 (codex r1): under ``grouping`` the modal pins direction and Konto,
      // so the pinned values must come from the record the plan GROUPS WITH, not
      // from the plan's own — a plan saved with a contradicting sign/Konto by the
      // pre-#3172 UI would otherwise be locked into its corruption (toggle and
      // select hidden, Save re-sending the contradiction with no affordance left
      // to correct it). Seeding from the sibling makes the pinned edit
      // self-repairing: an untouched Save re-aligns the plan with its commitment.
      // Precedence payed ?? contracted mirrors ``handleCategorize``'s commitment
      // resolution (the actual outranks the expectation); a sibling without an
      // account keeps the plan's own (nothing to split from — and with neither,
      // the modal's account-less escape keeps the select).
      const sibling = tx?.payed ?? tx?.contracted;
      // #3172 (review r2): re-aligning the SIGN must re-align the CATEGORY with
      // it. The modal's own ``setDirection`` drops a category the new sign no
      // longer offers back to ``unsorted``, but under ``grouping`` the toggle is
      // gone, so that repair can never run — and ``categoriesForPicker``
      // force-includes the stored value, which RENDERS the stale category as
      // though it were legitimate. Saving that posts e.g. income + exp_lease:
      // ``computeBalanceSeries`` reads the record's own ``direction`` (an
      // Einnahme on the curve) while ``categoryRollup`` keys the group off
      // CATEGORY_META (an Ausgabe in the Kategorien view) — the two surfaces
      // disagree by twice the amount. Same fallback as ``setDirection``, applied
      // where the sign is overridden.
      const initial = sibling
        ? {
            ...p,
            direction: sibling.direction,
            account_id: sibling.account_id ?? p.account_id,
            ledger_category: categoriesForPicker(sibling.direction).some((g) =>
              g.options.some((o) => o.id === p.ledger_category),
            )
              ? p.ledger_category
              : ('unsorted' as const),
          }
        : p;
      setForm({ mode: 'edit', initial, ...(sibling ? { grouping: true } : {}) });
    },
    [transactionById],
  );
  const handleCategoryTxClick = useCallback(
    (txId: string) => {
      const row = ledgerRowByTxId.get(txId);
      if (!row) return;
      switch (categorizeAction(row)) {
        case 'edit':
          if (row.planned) editPlanned(row.planned);
          break;
        case 'categorize':
          handleCategorize(row);
          break;
        case 'none':
          // Settled by contract (#3065) — the invoice's own document already
          // categorised it. Nothing to open.
          break;
      }
    },
    [ledgerRowByTxId, handleCategorize, editPlanned],
  );

  // Valid plan-date window for the form (phantom-row guard, codex #1364 P2a).
  // With a stored config the anchor/horizon are fixed → bound directly off the
  // series. In fallback (no-config) mode the synthetic anchor/horizon float with
  // the plan rows, so the window is computed from the OTHER rows' span (all plan
  // rows except the one being edited): the edit must keep the whole resulting set
  // inside one WINDOW_MONTHS window, or a re-anchor would drop some row off the
  // curve and leave a phantom ledger row.
  const anchored = Boolean(config?.anchor_date);
  const editingId = form?.initial?.derived_fact_id;
  const fallbackOther: FallbackOtherSpan = useMemo(() => {
    if (anchored) return undefined;
    const otherDates = planned
      .filter((p) => editingId == null || p.derived_fact_id !== editingId)
      .map((p) => p.value_date)
      .filter((d) => isIsoDate(d))
      .sort();
    if (otherDates.length === 0) return undefined;
    return { earliest: otherDates[0], latest: otherDates[otherDates.length - 1] };
  }, [anchored, editingId, planned]);
  const dateBounds = useMemo(
    () => deriveDateBounds(series, anchored, fallbackOther, today),
    [series, anchored, fallbackOther, today],
  );

  // The phantom-row guard's future-only lower bound is a FALSE positive for the
  // GROUPING flows ONLY: those records group with an existing sibling (deterministic
  // ``liq-plan-<txid>`` id, #1494 — the ``categorize`` act's payed sibling, or the
  // #3152 grouping-create's contracted expectation), so they are absorbed into that
  // transaction's single ledger row and can never become a phantom — even with a past
  // date (#1612 sub-04, extended by #3152). And ``computeBalanceSeries`` only buckets
  // planned rows ``> istHorizon``, so a grouped past-dated plan moves the curve exactly
  // zero — no phantom AND no money moves. Widen ``minExclusive`` to a day BEFORE the
  // prefilled (past) date so the native picker isn't clamped away from it and
  // ``buildPlannedInput`` accepts it.
  //
  // The relaxation MUST be gated two ways — NOT every prefilled date:
  //  (1) **Grouping paths only** — read straight off ``mode``/``grouping``, both set at
  //      the ONE place the act starts (#2678 made the act a first-class mode, replacing
  //      the ``create`` + ``initial.derived_fact_id != null`` inference this used to
  //      run; #3152's ``grouping`` flag follows the same rule for the payed-less
  //      grouping create AND for ``editPlanned``'s edit of a grouped plan, both set
  //      from the row's actual record composition). Without the gate, editing a
  //      planned-ONLY "did-not-happen" row dated ≤ anchor would lower the bound below
  //      its own date and let Save re-persist a date ``computeBalanceSeries`` drops —
  //      re-creating the very phantom the guard prevents. (A grouped plan's sibling
  //      record anchors its row, which is why edit-of-grouped is exempt.)
  //  (2) **Anchored (stored-config) mode only.** In fallback (no-config) mode the
  //      projection anchor is synthesized from the planned ``value_date``s, so a past
  //      categorized/grouped plan could become the new earliest, re-anchor the floating
  //      12-month window and drop later plans off the chart while the ledger still
  //      renders them (a phantom). Grouping does nothing to stop that — the anchor is
  //      computed from plan dates, not ledger rows. With a stored config the anchor is
  //      FIXED (``config.anchor_date``), so a past grouped record can never move it —
  //      only then is widening safe.
  // (Both gates from successive codex rounds on the #1612 PR.) ``max`` is always left
  // untouched.
  const prefilledDate =
    anchored && (form?.mode === 'categorize' || form?.grouping)
      ? form?.initial?.value_date
      : undefined;
  const effectiveBounds = useMemo(() => {
    if (!dateBounds || !prefilledDate || !isIsoDate(prefilledDate)) return dateBounds;
    if (prefilledDate > dateBounds.minExclusive) return dateBounds; // already inside the window
    return { ...dateBounds, minExclusive: addDays(prefilledDate, -1) };
  }, [dateBounds, prefilledDate]);

  return (
    <div className="liquiditaetsplanung-page">
      <LensePanel
        title={t('appPanels.liquiditaetsplanung', 'Liquiditätsplanung')}
        items={lensItems}
        selectedItem={expandedWj}
        // A selected YEAR highlights the WJ header itself (LensePanel marks the
        // top-level item active only when no sub-item is selected) — #2254.
        selectedSubItem={period.kind === 'month' ? selectedYm : null}
        onSelect={handleLensSelect}
        isMobileOpen={mobilePanel?.isOpen ?? false}
        onMobileClose={handleMobileClose}
      />
      <PageLoadOverlay
        visible={pageLoadOverlay.showOverlay}
        elapsedSeconds={pageLoadOverlay.elapsedSeconds}
      />
      <div className="liquiditaetsplanung-page__main">
        {/* Error wins over the empty state — a failed load otherwise
            reads as "this farm has no plan". */}
        {!isLoading && error != null && (
          <div className="liquiditaetsplanung-page__error" role="status">
            Hinweis: Einige Daten konnten nicht geladen werden.
          </div>
        )}

        {/* #2664 — the sole-bucket empty state, explained. After the
            revenue_bucket filter a wiberry-first tenant (only the synthetic
            fa-liquiditaet bucket, never a Kontoauszug) has no real Konto, so the
            whole surface below is legitimately empty. Bare, that reads as "this
            farm has no plan"; named, it points at the one action that fills it.
            Behind the same !error guard as the empty state — a failed read must
            never be reported as "you have not connected a bank account". */}
        {!isLoading && error == null && noBankAccountConnected && (
          <div
            className="liquiditaetsplanung-page__no-account"
            role="status"
            aria-label="Noch kein Bankkonto verbunden"
          >
            <strong>Noch kein Bankkonto verbunden.</strong> Für diesen Betrieb liegen bislang
            nur Umsatz-Aggregate vor, aber kein Bankkonto. Laden Sie einen Kontoauszug hoch,
            um Kontostand, Ist-Horizont und Liquiditätskurve zu sehen.
          </div>
        )}

        {/* #3148 (c) — the #2611 page-level "Achtung: unvollständige Daten" banner
            is gone by decision (owner-filed). The quieter column-mode truncation
            note inside BalanceChart stays: it warns about a DIFFERENT failure
            (bars silently shorten; the curve merely flat-carries), so the hook's
            ``dataQuality`` remains threaded below. */}
        {!isLoading && error == null && (
          <>
            {/* #2160 — the account switcher is folded into the balance list below
                (the breakdown rows are the selectable segments); the top chip bar is
                gone. Pass the selection wiring ONLY for a multi-account tenant so a
                single-account (D4) list stays a static, non-pressable breakdown. */}
            <LiquiditySummary
              verdict={scopedSeries.verdict}
              summary={summary}
              breakdown={accountBreakdown}
              // HOF-97 criterion 7 — the incompleteness disclosure on the tile carrying
              // the number the customer actually quoted, plus the §6 L5 reconciliation
              // verdict for the series currently in scope.
              //
              // HOF-135 §1.6 — ``balanceTruncated``, not ``truncated``. The OR across all
              // seven feeds answers "is any part of this page incomplete?"; over a euro
              // figure the only honest question is whether a truncated feed reached THIS
              // number. On WF it did not: the 10k cap fires on the bank FACTS, which feed
              // the Ledger, while the curve reads ``cash_flow`` through the server-side
              // aggregate. See ``LiquidityDataQuality.balanceTruncated``.
              truncated={dataQuality.balanceTruncated}
              reconciled={scopedSeries.reconciled}
              statementResiduals={scopedSeries.statementResiduals}
              // HOF-5 — the balances are read at the selected period's end, so the
              // header names that date whenever it is not simply today. Marked as
              // Prognose past the Ist horizon: the figure is then a projection,
              // and "Stand" would claim it was banked.
              {...(breakdownAsOf !== today
                ? {
                    asOfDate: breakdownAsOf,
                    asOfIsForecast:
                      scopedSeries.istHorizon !== '' && breakdownAsOf > scopedSeries.istHorizon,
                  }
                : {})}
              {...(isMultiAccount
                ? { selectedId: selectedKonto, onSelectAccount: handleKontoChange }
                : {})}
              headerAction={
                <button
                  type="button"
                  className="liquiditaetsplanung-page__new"
                  onClick={() => setForm({ mode: 'create' })}
                >
                  <Plus size={16} /> Neu Zahlung
                </button>
              }
            />
            <section className="liquiditaetsplanung-page__chart">
              <BalanceChart
                dailySeries={scopedSeries.dailySeries}
                kkLimitEur={scopedSeries.verdict.kkLimitEur}
                todayYm={todayYm}
                today={today}
                selectedYm={selectedYm || undefined}
                // #2254: a selected Wirtschaftsjahr bands its full range
                // (clamped to the chart window); month selections keep the
                // single-month band via selectedYm.
                bandRange={wjBandRange}
                txByDate={txByDate}
                selectedMarker={selectedMarker}
                onSelectTx={setSelectedTxId}
                onSelectMonth={handleMonthChange}
                // #2684 Part B — the Säulen presentation aggregates THESE rows
                // (the ones the Buchungen list below renders), so the bars and
                // the list reconcile by construction.
                ledgerRows={ledgerRows}
                istHorizon={scopedSeries.istHorizon}
                presentation={presentation}
                onPresentationChange={handlePresentationChange}
                // HOF-135 §1.6 — the Säulen are the same money as the curve, drawn
                // monthly, so they take the same balance-scoped flag: a feed that never
                // reached the series cannot have made a column too low.
                truncated={dataQuality.balanceTruncated}
              />
              {summary.istStandDate && (
                <p className="liquiditaetsplanung-page__forecast-caption">
                  Ist bis {shortDay(summary.istStandDate)} · Prognose ab{' '}
                  {shortDay(summary.prognoseStartDate)}
                  {/* HOF-5 — the lag clause. The rendered boundary follows the
                      farm's LATEST confirmed booking, so a Konto whose
                      Kontoauszug ends earlier no longer drags the whole curve
                      back (WF: „Ist bis 30.06." over bank facts running to
                      30.07). That drift is not swallowed — it is stated here, by
                      date, which is a louder signal than the dashed line it
                      replaces: ``verdictHorizon`` is the strict MIN across every
                      Konto, so the two differing IS the lag. */}
                  {lagUntilDate && ` · einzelne Konten nur bis ${shortDay(lagUntilDate)}`}
                </p>
              )}
            </section>
            <section className="liquiditaetsplanung-page__ledger">
              {/* Unified feed header: the view switcher on the left and the column
                  labels on the right share one line, so neither leaves a dead gap.
                  The labels reuse each feed's own head geometry, so they stay aligned
                  with the figures below and keep a stable right edge across the switch. */}
              <div className="liquiditaetsplanung-page__feed-header">
                <div
                  className="liquiditaetsplanung-page__view-switch"
                  role="group"
                  aria-label="Ansicht"
                >
                  <button
                    type="button"
                    className="liquiditaetsplanung-page__view-segment"
                    aria-pressed={view === 'buchungen'}
                    onClick={() => handleViewChange('buchungen')}
                  >
                    Buchungen
                  </button>
                  <button
                    type="button"
                    className="liquiditaetsplanung-page__view-segment"
                    aria-pressed={view === 'kategorien'}
                    onClick={() => handleViewChange('kategorien')}
                  >
                    Kategorien
                  </button>
                </div>
                {unsorted.count > 0 && (
                  <button
                    type="button"
                    className="liquiditaetsplanung-page__unsorted-chip"
                    onClick={() => handleViewChange('buchungen')}
                  >
                    ⚠ {unsorted.count} unsortiert · {formatEur(unsorted.totalEur)}
                  </button>
                )}
                {view === 'kategorien' ? (
                  <div className="liquiditaetsplanung-catfeed__head" aria-hidden="true">
                    {/* ONE period label (#2254): the selected month's name, or the
                        selected Wirtschaftsjahr's label — never both. */}
                    <span className="liquiditaetsplanung-catfeed__head-period">
                      {period.kind === 'wj'
                        ? wjLabel(period.startYear)
                        : selectedYm
                          ? formatMonthName(selectedYm)
                          : 'Zeitraum'}
                    </span>
                  </div>
                ) : (
                  <div className="liquiditaetsplanung-ledger__head" aria-hidden="true">
                    <span className="liquiditaetsplanung-ledger__head-amount">Betrag</span>
                    <span className="liquiditaetsplanung-ledger__head-balance">Kontostand</span>
                  </div>
                )}
              </div>
              {/* HOF-135 §1.6 — the truncation disclosure, on the surface the truncated
                  feed actually serves. The balance tile above now carries only
                  ``balanceTruncated``; without this the WF shape (bank facts capped at
                  10.000, curve fed by the cash_flow aggregate) would disclose NOTHING
                  anywhere, trading a mis-scoped warning for a missing one. Names the
                  feed, because „nicht alle Buchungen" with no subject is the #2611
                  lesson — a warning that says *something* is incomplete without ever
                  saying what — one surface over. Scoped by ``ledgerTruncated``, not the
                  all-feed OR: a truncated ``financial_account`` or statement read leaves
                  THIS list complete, and claiming otherwise would repeat the very defect
                  this change fixes, one surface over. */}
              {dataQuality.ledgerTruncated && (
                <p className="liquiditaetsplanung-ledger__note" role="status">
                  Unvollständige Daten: es konnten nicht alle Buchungen geladen werden —
                  diese Liste ist unvollständig
                  {dataQuality.truncatedSources.length > 0
                    ? ` (${dataQuality.truncatedSources.join(', ')})`
                    : ''}
                  .
                </p>
              )}
              {view === 'kategorien' ? (
                <CategoryFeed
                  records={scopedRecords}
                  period={period}
                  wjStartMonth={wjStartMonth}
                  todayYm={todayYm}
                  // #3158: the scope's Ist/Prognose boundary (under Alle Konten
                  // the MIN over accounts with post-anchor actuals, #3241 — the
                  // same grading the caption uses), so
                  // a fully-post-horizon month drops its structural-zero
                  // "Ist bisher" + the deltas derived from it.
                  istHorizon={scopedSeries.istHorizon}
                  expandedGroups={catExpandedGroups}
                  onToggleGroup={toggleCatGroup}
                  openCategoryId={catOpenId}
                  onToggleCategory={toggleCatOpen}
                  onTransactionClick={handleCategoryTxClick}
                  // #2347: the Unsortiert row deep-links into the Buchungen ledger's
                  // categorise flow, mirroring the header ⚠ chip above.
                  onShowUnsorted={() => handleViewChange('buchungen')}
                />
              ) : (
                <LedgerFeed
                  rows={periodRows}
                  // #2211: the below-limit flags use the ACCOUNT's own KK-limit when
                  // scoped, else the tenant total's under Alle Konten. Same
                  // ``scopedSeries`` the chart is handed, so the two cannot disagree.
                  kkLimitEur={scopedSeries.verdict.kkLimitEur}
                  onEditPlanned={editPlanned}
                  onCategorize={handleCategorize}
                  selectedId={selectedTxId}
                  onSelect={setSelectedTxId}
                  // #2211: surface each row's Konto (multi-account only).
                  accountLabelById={accountLabelById}
                />
              )}
            </section>
          </>
        )}
      </div>

      {form && (
        <TransactionFormModal
          mode={form.mode}
          initial={form.initial}
          bounds={effectiveBounds}
          // #1616's ``lockDate`` prop is gone: the modal derives the booked-date policy
          // (and the read-only Betrag/Konto/Buchungstext context) from ``mode`` itself,
          // which is the same predicate that gates ``effectiveBounds`` above (#2678).
          // #2211: the Konto select (multi-account only) + its preselected default
          // (selected Konto, else overlay-primary; an edit's own account wins inside
          // the modal). Single-account tenants pass nothing → identical markup.
          accounts={isMultiAccount ? accounts : undefined}
          defaultAccountId={defaultAccountId}
          // #3172: thread the #3152 grouping flag (both writers — handleCategorize's
          // payed-less branch and editPlanned) so the modal narrows the create-mode
          // affordances grouped semantics contradict (Konto → read-only context,
          // no Richtung toggle; Datum deliberately stays).
          grouping={form.grouping}
          onSubmit={handleFormSubmit}
          onClose={closeForm}
          submitting={upsert.isPending}
          serverError={serverError}
        />
      )}
    </div>
  );
}
